import React, { useState, useEffect } from "react";
import "../inward-styles.css";
import Icon from "@mdi/react";
import {
  mdiMagnify,
  mdiChevronDown,
  mdiChevronUp,
  mdiAlertOutline,
  mdiCloseCircleOutline,
} from "@mdi/js";
import { useAuth } from "../../../../context_providers/auth/authProvider";
import LoadingWidget from "../../../../context_providers/modal/loader";
import PaginationWarehouse from "../inward/PaginationWarehouse";
import {
  fetchExportOutwardByEmail,
  useWarehouseOutward,
  WarehouseOutwardProvider,
} from "../contexts/OutwardContext";
import { mdiEmailOutline } from "@mdi/js";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import OutwardOrdersTable from "./OutwardOrdersTable";
import WarehouseSearchFieldByNameID from "../common-components/WarehouseSearchFieldByNameID";
import { useWarehouseViewOnly } from "../hooks/useWarehouseViewOnly";
import MultiSelectDropdown from "../common-components/MultiSelectDropdown";
import ItemSearchTagInput from "../common-components/ItemSearchTagInput";

const WarehouseOutwardDashboardWrapper = () => {
  const [activeTab, setActiveTab] = useState<TabType>("outward-active");

  return (
    <WarehouseOutwardProvider
      config={{
        initialTab: "outward-active",
        storageKey: "warehouse:outward:filters:v2",
      }}
    >
      <WarehouseOutwardDashboard
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
    </WarehouseOutwardProvider>
  );
};

export default WarehouseOutwardDashboardWrapper;
type TabType = "outward-active" | "outward-rejected";
interface Props {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
}

const WarehouseOutwardDashboard: React.FC<Props> = ({
  activeTab,
  setActiveTab,
}) => {
  const { hasRoleTitle } = useAuth();
  const isWarehouseViewOnly = useWarehouseViewOnly();
  const {
    outwardList,
    isLoading,
    filters,
    setFilters,
    page,
    setPage,
    pagination,
    statusCount,
  } = useWarehouseOutward();

  const [showFilters, setShowFilters] = useState(window.innerWidth >= 768);
  const [canUpdateOutward, setCanUpdateOutward] = useState(false);
  const [cascade, setCascade] = useState(false);
  const [cascadeHistory, setCascadeHistory] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { showToast } = useToast();

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await fetchExportOutwardByEmail({
        search: filters.search,
        status: filters.status?.length ? filters.status.join(",") : undefined,
        type: filters.type?.length ? filters.type.join(",") : undefined,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        fromWarehouse: filters.fromWarehouse,
        toWarehouse: filters.toWarehouse,
        itemSearch: filters.itemSearch,
        tab: filters.tab || activeTab,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        includeDispatchedDate: true,
      });
      if (res?.success) {
        showToast({
          type: ToastType.success,
          text: res.message || "Export will be emailed shortly.",
        });
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to schedule export",
        });
      }
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e?.message || "Failed to schedule export",
      });
    } finally {
      setExporting(false);
    }
  };

  const statusOptions = [
    "WH Yet To Be Assigned",
    "ST FG Approval Pending",
    "Device Allocation Pending",
    "Material Picking Pending",
    "Doc Pending",
    "Ready To Dispatch",
    "Tracking ID Pending",
    "Dispatched",
    "Delivered",
    "Cancelled",
    "Returned",
  ];

  useEffect(() => {
    const isFinanceTeam = hasRoleTitle({
      roleTitle: "warehouse module finance",
    });

    setCanUpdateOutward(!isFinanceTeam && !isWarehouseViewOnly);

    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setShowFilters(!mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [hasRoleTitle, isWarehouseViewOnly]);

  return (
    <main
      id="warehouse-outward-dashboard"
      className="wh-mobile-shrink p-4 md:p-6 animate-fade-in"
    >
      <div className="flex flex-wrap justify-between items-center mb-6 gap-3">
        <h1 className="text-3xl font-bold border-l-4 border-myPrimaryColor pl-2">
          Outward Management
        </h1>
        <button
          id="warehouse-outward-export-btn"
          onClick={handleExport}
          disabled={exporting}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Icon path={mdiEmailOutline} size={0.8} />
          {exporting ? "Scheduling…" : "Export by Email"}
        </button>
      </div>
      <div className="space-y-6 mb-4">
        <div className="overflow-x-auto pb-2">
          <div className="flex gap-4 min-w-max">
            {[
              {
                label: "Warehouse Yet to Be Assigned",
                value: statusCount["WH Yet To Be Assigned"],
                border: "border-yellow-800",
              },
              {
                label: "Device Allocation Pending",
                value: statusCount["Device Allocation Pending"],
                border: "border-red-500",
              },

              {
                label: "Doc Pending",
                value: statusCount["Doc Pending"],
                border: "border-purple-500",
              },
              {
                label: "Ready",
                value: statusCount["Ready To Dispatch"],
                border: "border-green-500",
              },
              {
                label: "Tracking ID Pending",
                value: statusCount["Tracking ID Pending"],
                border: "border-yellow-500",
              },
              {
                label: "Dispatched",
                value: statusCount.Dispatched,
                border: "border-blue-700",
              },
              {
                label: "Delivered",
                value: statusCount.Delivered,
                border: "border-emerald-600",
              },
              {
                label: "Rejected",
                value: statusCount.Rejected,
                border: "border-red-600",
              },
              {
                label: "Cancelled",
                value: statusCount.Cancelled,
                border: "border-gray-500",
              },
              {
                label: "Returned",
                value: statusCount.Returned,
                border: "border-orange-500",
              },
            ].map(({ label, value, border }) => (
              <div
                key={label}
                id={`warehouse-outward-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`w-52 rounded-lg border-l-4 bg-card bg-white text-card-foreground shadow-sm ${border}`}
              >
                <div className="p-4">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-2xl font-bold">{value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex gap-2 bg-blue-50 p-2 rounded-md mb-4">
        {[
          {
            key: "outward-active",
            label: `Active Orders`,
          },
          { key: "outward-rejected", label: "Rejected SO" },
        ].map((t) => (
          <button
            key={t.key}
            id={`warehouse-outward-tab-${t.key.replace("outward-", "")}`}
            onClick={() => {
              setActiveTab(t.key as TabType);
              setPage(1); // Reset page on tab change
              if (t.key === "outward-rejected") {
                setFilters((prev) => ({
                  ...prev,
                  type: ["SO"],
                  status: [],
                  tab: t.key as TabType,
                }));
              } else {
                setFilters((prev) => ({
                  ...prev,
                  type: [],
                  status: [],
                  tab: t.key as TabType,
                }));
              }
            }}
            className={`px-4 py-2 rounded text-sm font-bold ${
              activeTab === t.key ? "bg-white shadow-sm" : "text-gray-600"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {activeTab === "outward-active" && (
        <div className="flex items-center justify-between mb-4">
          <div
            id="warehouse-outward-filters-toggle"
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setShowFilters((p) => !p)}
          >
            <div className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              Filters
              <Icon
                path={showFilters ? mdiChevronUp : mdiChevronDown}
                size={0.8}
              />
            </div>
          </div>

          {(filters.search ||
            filters.status?.length ||
            filters.type?.length ||
            filters.fromDate ||
            filters.toDate ||
            filters.fromWarehouse ||
            filters.toWarehouse ||
            filters.itemSearch) && (
            <button
              id="warehouse-outward-clear-filters-btn"
              onClick={() =>
                setFilters({
                  search: "",
                  status: [],
                  type: [],
                  fromDate: "",
                  toDate: "",
                  fromWarehouse: "",
                  toWarehouse: "",
                  itemSearch: "",
                })
              }
              className="text-sm font-semibold text-red-600 hover:text-red-700 flex items-center gap-1 transition-colors"
            >
              <Icon path={mdiCloseCircleOutline} size={0.6} />
              Clear All Filterss
            </button>
          )}
        </div>
      )}
      {showFilters && activeTab === "outward-active" && (
        <div className="rounded-lg border border-gray-300 shadow-sm bg-white mb-4">
          {/* ROW 1 */}
          <div className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 flex items-center gap-2">
                <div className="relative flex-1">
                  <Icon
                    path={mdiMagnify}
                    className="absolute left-3 top-1/3 -translate-y-1/2 h-5 w-5 text-muted-foreground"
                  />
                  <input
                    id="warehouse-outward-filter-search"
                    className="flex h-10 w-full rounded-md border bg-background px-3 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Search by Order ID, Tracking ID or Complaint ID..."
                    value={filters.search || ""}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        search: e.target.value.trim(),
                      }))
                    }
                  />
                </div>

                {filters.search && (
                  <button
                    id="warehouse-outward-filter-search-clear"
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, search: "" }))
                    }
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    title="Clear Search"
                  >
                    <Icon path={mdiCloseCircleOutline} size={0.7} />
                  </button>
                )}
              </div>

              <MultiSelectDropdown
                id="warehouse-outward-filter-status"
                placeholder="All Status"
                values={filters.status || []}
                onChange={(next) =>
                  setFilters((prev) => ({ ...prev, status: next }))
                }
                options={statusOptions.map((s) => ({
                  value: s,
                  label: s,
                }))}
              />

              <MultiSelectDropdown
                id="warehouse-outward-filter-type"
                placeholder="All Types"
                values={filters.type || []}
                onChange={(next) =>
                  setFilters((prev) => ({ ...prev, type: next }))
                }
                options={[
                  { value: "SO", label: "Sales Order" },
                  { value: "ST", label: "Stock Transfer" },
                ]}
              />
            </div>
          </div>

          {/* ROW 2 */}
          <div className="p-4 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <input
                  id="warehouse-outward-filter-from-date"
                  type="date"
                  value={filters.fromDate || ""}
                  onChange={(e) =>
                    setFilters((p) => ({
                      ...p,
                      fromDate: e.target.value,
                    }))
                  }
                  className="h-10 w-full rounded-md border px-3 text-sm"
                  placeholder="From Date"
                />

                {filters.fromDate && (
                  <button
                    onClick={() => setFilters((p) => ({ ...p, fromDate: "" }))}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Icon path={mdiCloseCircleOutline} size={0.7} />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="warehouse-outward-filter-to-date"
                  type="date"
                  value={filters.toDate || ""}
                  onChange={(e) =>
                    setFilters((p) => ({
                      ...p,
                      toDate: e.target.value,
                    }))
                  }
                  className="h-10 w-full rounded-md border px-3 text-sm"
                  placeholder="To Date"
                />

                {filters.toDate && (
                  <button
                    onClick={() => setFilters((p) => ({ ...p, toDate: "" }))}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Icon path={mdiCloseCircleOutline} size={0.7} />
                  </button>
                )}
              </div>

              {/* From Warehouse */}
              <WarehouseSearchFieldByNameID
                id="warehouse-outward-filter-from-warehouse"
                placeholder="From Warehouse (Name)"
                value={filters.fromWarehouse || ""}
                onChange={(val) =>
                  setFilters((p) => ({
                    ...p,
                    fromWarehouse: val,
                  }))
                }
                onClear={() => setFilters((p) => ({ ...p, fromWarehouse: "" }))}
              />

              {/* To Warehouse */}
              <WarehouseSearchFieldByNameID
                id="warehouse-outward-filter-to-warehouse"
                placeholder="To Warehouse (Name)"
                value={filters.toWarehouse || ""}
                onChange={(val) =>
                  setFilters((p) => ({
                    ...p,
                    toWarehouse: val,
                  }))
                }
                onClear={() => setFilters((p) => ({ ...p, toWarehouse: "" }))}
              />
            </div>
          </div>

          {/* ROW 3 */}
          <div className="p-4 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
              {/* 50% */}
              <div className="md:col-span-2">
                <ItemSearchTagInput
                  id="warehouse-outward-filter-item-search"
                  value={filters.itemSearch || ""}
                  onChange={(next) =>
                    setFilters((p) => ({ ...p, itemSearch: next }))
                  }
                />
              </div>

              <div className="inline-flex items-center gap-2 h-10 select-none">
                <input
                  id="warehouse-outward-filter-expand-items"
                  type="checkbox"
                  checked={cascade}
                  onChange={(e) => setCascade(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />

                <span className="text-sm text-gray-700">Expand items</span>
              </div>

              <div className="inline-flex items-center gap-2 h-10 select-none">
                <input
                  id="warehouse-outward-filter-expand-history"
                  type="checkbox"
                  checked={cascadeHistory}
                  onChange={(e) => setCascadeHistory(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />

                <span className="text-sm text-gray-700">Expand history</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ==================== TABLE for active tab ==================== */}
      {activeTab === "outward-active" &&
        outwardList &&
        outwardList.length > 0 && (
          <>
            <OutwardOrdersTable
              updatesAllowed={canUpdateOutward}
              cascade={cascade}
              cascadeHistory={cascadeHistory}
            />

            <div className="mt-8"></div>

            <PaginationWarehouse
              id="warehouse-outward-pagination"
              page={page}
              totalPages={pagination.totalPages}
              onPageChange={(newPage) => {
                if (newPage >= 1 && newPage <= pagination.totalPages) {
                  setPage(newPage);
                }
              }}
            />
          </>
        )}
      {activeTab === "outward-active" &&
        outwardList &&
        outwardList.length === 0 &&
        !isLoading && (
          <p className="text-center text-muted-foreground mt-10">
            No outward orders found.
          </p>
        )}
      {activeTab === "outward-rejected" && (
        <div className="flex items-start gap-3 rounded-lg border info-card-red p-3 mb-4">
          <Icon path={mdiAlertOutline} size={1} />

          <div className="text-sm">
            <p className="font-semibold">Rejected Sales Orders</p>
            <p className="mt-1">
              These Sales Orders have been rejected by Finance. Device IDs
              allocated to these orders have been{" "}
              <span className="font-semibold">RELEASED</span> and are available
              for reallocation. Please coordinate with the sales team to resolve
              issues and create new orders if needed.
            </p>
          </div>
        </div>
      )}
      {/* ==================== TABLE for rejected tab ==================== */}
      {activeTab === "outward-rejected" && outwardList.length > 0 && (
        <>
          <OutwardOrdersTable
            updatesAllowed={false}
            title={"Rejected Sales Orders"}
            subTitle={
              "Review rejection reasons and take appropriate action outside the system"
            }
            icon={mdiCloseCircleOutline}
            cascade={cascade}
            cascadeHistory={cascadeHistory}
          />

          <div className="mt-8"></div>

          <PaginationWarehouse
            id="warehouse-outward-rejected-pagination"
            page={page}
            totalPages={pagination.totalPages}
            onPageChange={(newPage) => {
              if (newPage >= 1 && newPage <= pagination.totalPages) {
                setPage(newPage);
              }
            }}
          />
        </>
      )}
      {activeTab === "outward-rejected" &&
        outwardList &&
        outwardList.length === 0 &&
        !isLoading && (
          <p className="text-center text-muted-foreground mt-10">
            No Rejected outward orders found.
          </p>
        )}
      {/* ==================== LOADING OVERLAY ==================== */}
      {isLoading && <LoadingWidget />}
    </main>
  );
};
